/**
 * Angular service for the Post Reactions API
 *
 * @remarks
 * Manages emoji reactions on individual blog posts.  Each authenticated user
 * may hold at most one reaction per post per org — submitting a new type
 * replaces (upserts) the previous one.
 *
 * Reaction data is stored per-post in signal-based Maps so multiple posts
 * can be tracked simultaneously (e.g. on a feed page).  Optimistic updates
 * are applied on every mutation and rolled back on API failure.
 *
 * The GET endpoint is public (no auth required) but the `orgId` query param
 * is mandatory; optionally pass `userId` to receive the caller's current
 * reaction in the response.  POST / DELETE require a Bearer token, handled
 * by the consuming application's `authInterceptor`.
 *
 * @example
 * ```typescript
 * // app.config.ts
 * providers: [
 *   providePostReactionService({ apiBaseUrl: environment.cmsApiBaseUrl })
 * ]
 *
 * // In a component
 * readonly reactionSvc = inject(PostReactionService);
 *
 * ngOnInit() {
 *   this.reactionSvc
 *     .getReactions(this.postId, this.orgId, this.currentUserId)
 *     .subscribe();
 * }
 *
 * onReact(type: PostReactionType) {
 *   this.reactionSvc
 *     .upsertReaction(this.postId, { orgId: this.orgId, reactionType: type })
 *     .subscribe();
 * }
 * ```
 *
 * @see POST_REACTIONS_ANGULAR_SERVICE.md for the full API reference
 */

import {
  Injectable,
  InjectionToken,
  computed,
  inject,
  signal
} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import {
  PostReactionCounts,
  PostReactionEntry,
  PostReactionType,
  PostReactionsResponse,
  ReactionActionResponse,
  UpsertReactionPayload
} from '../models/post-reaction.model';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Configuration interface for PostReactionService
 */
export interface PostReactionConfig {
  /**
   * Base URL for the API (without trailing slash)
   * @example 'http://localhost:3001'
   * @example 'https://api.example.com'
   */
  apiBaseUrl: string;

  /**
   * Enable debug logging to the console
   * @default false
   */
  debug?: boolean;
}

/**
 * Injection token for PostReactionService configuration.
 *
 * Provide this token in your `app.config.ts`:
 * ```typescript
 * {
 *   provide: POST_REACTION_API_CONFIG,
 *   useValue: { apiBaseUrl: environment.cmsApiBaseUrl }
 * }
 * ```
 * Or use the `providePostReactionService()` helper.
 */
export const POST_REACTION_API_CONFIG = new InjectionToken<PostReactionConfig>(
  'POST_REACTION_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => {
      console.warn(
        '[PostReactionService] POST_REACTION_API_CONFIG not provided. ' +
        'Using empty apiBaseUrl. Provide the token or call providePostReactionService().'
      );
      return { apiBaseUrl: '', debug: false };
    }
  }
);

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Service for managing emoji reactions on blog posts.
 *
 * Stores per-post state in two signal Maps so multiple posts on the same page
 * (e.g. a feed) can all be tracked reactively at once.
 */
@Injectable({ providedIn: 'root' })
export class PostReactionService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(POST_REACTION_API_CONFIG);
  private readonly _apiBase = this._config.apiBaseUrl;

  // -------------------------------------------------------------------------
  // Reactive state  (Map<postId, …>)
  // -------------------------------------------------------------------------

  /**
   * Map: postId → reaction count breakdown.
   * Updated by `getReactions()`, `upsertReaction()`, and `removeReaction()`.
   */
  private readonly _countsMap = signal<Map<string, PostReactionCounts>>(
    new Map()
  );

  /**
   * Map: postId → current user's reaction (or null).
   * Updated by `getReactions()`, `upsertReaction()`, and `removeReaction()`.
   */
  private readonly _userReactionMap = signal<
    Map<string, PostReactionType | null>
  >(new Map());

  /**
   * Returns a computed signal for the reaction counts of a single post.
   *
   * Use inside a template or another `computed()`:
   * ```html
   * {{ countsFor(postId)()?.like ?? 0 }}
   * ```
   *
   * @param postId - MongoDB ObjectId string of the blog post
   */
  countsFor(postId: string): ReturnType<typeof computed<PostReactionCounts | undefined>> {
    return computed(() => this._countsMap().get(postId));
  }

  /**
   * Returns a computed signal for the current user's reaction on a single post
   * (or null when the user has not reacted or is not authenticated).
   *
   * @param postId - MongoDB ObjectId string of the blog post
   */
  userReactionFor(postId: string): ReturnType<typeof computed<PostReactionType | null>> {
    return computed(() => this._userReactionMap().get(postId) ?? null);
  }

  /**
   * Returns a computed signal holding both counts and userReaction for a post.
   * Useful when a component needs to react to either field changing.
   *
   * @param postId - MongoDB ObjectId string of the blog post
   */
  reactionEntryFor(postId: string): ReturnType<typeof computed<PostReactionEntry | undefined>> {
    return computed(() => {
      const counts = this._countsMap().get(postId);
      if (!counts) return undefined;
      return {
        counts,
        userReaction: this._userReactionMap().get(postId) ?? null
      };
    });
  }

  // -------------------------------------------------------------------------
  // API methods
  // -------------------------------------------------------------------------

  /**
   * GET /api/posts/:postId/reactions
   *
   * Fetches reaction counts and the caller's current reaction.
   * This endpoint is public — no auth header is required — but `orgId` must
   * be supplied.  Pass `userId` (the caller's MongoDB user ID) to receive the
   * `userReaction` field.
   *
   * Populates `countsFor(postId)` and `userReactionFor(postId)` signals.
   *
   * @param postId - MongoDB ObjectId string of the blog post
   * @param orgId  - Organisation MongoID
   * @param userId - Optional MongoDB user ID for the authenticated caller
   */
  getReactions(
    postId: string,
    orgId: string,
    userId?: string
  ): Observable<PostReactionsResponse> {
    let params = new HttpParams().set('orgId', orgId);
    if (userId) params = params.set('userId', userId);

    this._log('getReactions', { postId, orgId, userId });

    return this._http
      .get<PostReactionsResponse>(
        `${this._apiBase}/api/posts/${postId}/reactions`,
        { params }
      )
      .pipe(
        tap(({ data }) => {
          this._patchEntry(postId, data.counts, data.userReaction);
          this._log('getReactions — success', postId, data.counts);
        }),
        catchError((err: unknown) => {
          this._log('getReactions — error', postId, err);
          return throwError(() => err);
        })
      );
  }

  /**
   * POST /api/posts/:postId/reactions
   *
   * Adds or updates (upserts) the authenticated user's reaction on a post.
   * If the user previously reacted with a different type, the old reaction is
   * replaced; if the server is unreachable the optimistic update is reverted.
   *
   * @param postId  - MongoDB ObjectId string of the blog post
   * @param payload - `{ orgId, reactionType }`
   */
  upsertReaction(
    postId: string,
    payload: UpsertReactionPayload
  ): Observable<ReactionActionResponse> {
    const prevReaction = this._userReactionMap().get(postId) ?? null;
    // Fall back to all-zero counts if getReactions hasn't been called yet
    // so the optimistic update always fires regardless of load order.
    const prevCounts: PostReactionCounts = this._countsMap().get(postId)
      ?? { like: 0, love: 0, insightful: 0, funny: 0 };

    // Optimistic update: decrement previous type, increment new type
    const next: PostReactionCounts = { ...prevCounts };
    if (prevReaction) {
      next[prevReaction] = Math.max(0, next[prevReaction] - 1);
    }
    next[payload.reactionType] = (next[payload.reactionType] ?? 0) + 1;
    this._patchEntry(postId, next, payload.reactionType);
    this._log('upsertReaction — optimistic', postId, payload.reactionType);

    return this._http
      .post<ReactionActionResponse>(
        `${this._apiBase}/api/posts/${postId}/reactions`,
        payload
      )
      .pipe(
        tap(() =>
          this._log('upsertReaction — confirmed', postId, payload.reactionType)
        ),
        catchError((err: unknown) => {
          // Roll back to state before optimistic update
          this._patchEntry(postId, prevCounts, prevReaction);
          this._log('upsertReaction — rolled back', postId);
          return throwError(() => err);
        })
      );
  }

  /**
   * DELETE /api/posts/:postId/reactions?orgId=
   *
   * Removes the authenticated user's reaction from a post.
   *
   * @param postId - MongoDB ObjectId string of the blog post
   * @param orgId  - Organisation MongoID
   */
  removeReaction(
    postId: string,
    orgId: string
  ): Observable<ReactionActionResponse> {
    const prevReaction = this._userReactionMap().get(postId) ?? null;
    // Fall back to all-zero counts if getReactions hasn't been called yet.
    const prevCounts: PostReactionCounts = this._countsMap().get(postId)
      ?? { like: 0, love: 0, insightful: 0, funny: 0 };

    // Optimistic update: decrement the removed type
    if (prevReaction) {
      const next: PostReactionCounts = {
        ...prevCounts,
        [prevReaction]: Math.max(0, prevCounts[prevReaction] - 1)
      };
      this._patchEntry(postId, next, null);
      this._log('removeReaction — optimistic', postId, prevReaction);
    }

    const params = new HttpParams().set('orgId', orgId);
    return this._http
      .delete<ReactionActionResponse>(
        `${this._apiBase}/api/posts/${postId}/reactions`,
        { params }
      )
      .pipe(
        tap(() => this._log('removeReaction — confirmed', postId)),
        catchError((err: unknown) => {
          // Roll back
          this._patchEntry(postId, prevCounts, prevReaction);
          this._log('removeReaction — rolled back', postId);
          return throwError(() => err);
        })
      );
  }

  /**
   * Toggle a reaction: removes the current reaction if `type` matches it,
   * otherwise upserts `type` as the new reaction.
   *
   * This is a convenience wrapper around `upsertReaction()` / `removeReaction()`.
   *
   * @param postId - MongoDB ObjectId string of the blog post
   * @param orgId  - Organisation MongoID
   * @param type   - The reaction type the user clicked
   */
  toggleReaction(
    postId: string,
    orgId: string,
    type: PostReactionType
  ): Observable<ReactionActionResponse> {
    const current = this._userReactionMap().get(postId) ?? null;
    return current === type
      ? this.removeReaction(postId, orgId)
      : this.upsertReaction(postId, { orgId, reactionType: type });
  }

  /**
   * Seed the signal maps with pre-fetched reaction data (e.g. from a resolver).
   * Skips any HTTP call — use when the data is already available (SSR transfer cache).
   *
   * @param postId      - MongoDB ObjectId string of the blog post
   * @param counts      - Reaction count breakdown
   * @param userReaction - Current user's reaction, or null
   */
  seedReactions(
    postId: string,
    counts: PostReactionCounts,
    userReaction: PostReactionType | null
  ): void {
    this._patchEntry(postId, counts, userReaction);
    this._log('seedReactions', postId, counts);
  }

  /**
   * Remove a post's reaction state from the local signal maps.
   * Call when navigating away from a post to free memory in long-lived sessions.
   *
   * @param postId - MongoDB ObjectId string of the blog post
   */
  evictPost(postId: string): void {
    this._countsMap.update((m) => {
      const next = new Map(m);
      next.delete(postId);
      return next;
    });
    this._userReactionMap.update((m) => {
      const next = new Map(m);
      next.delete(postId);
      return next;
    });
    this._log('evictPost', postId);
  }

  /**
   * Clear all cached reaction state.
   * Call on user logout or organisation switch.
   */
  reset(): void {
    this._countsMap.set(new Map());
    this._userReactionMap.set(new Map());
    this._log('state reset');
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private _patchEntry(
    postId: string,
    counts: PostReactionCounts,
    userReaction: PostReactionType | null
  ): void {
    this._countsMap.update((m) => new Map(m).set(postId, counts));
    this._userReactionMap.update((m) => new Map(m).set(postId, userReaction));
  }

  private _log(message: string, ...args: unknown[]): void {
    if (this._config.debug) {
      console.log(`[PostReactionService] ${message}`, ...args);
    }
  }
}
